// Capture metadata recorded at the shutter and sent with each upload (SF-84).
//
// The API stores one record per upload attempt as a blob with no expiry, so
// the camera tilt and device of every photo can later be joined with the tape.
// Kept free of browser globals so it can be tested in node.
//
// Conventions:
//   - gravity is DeviceMotionEvent.accelerationIncludingGravity, device frame
//     (x right, y up the screen, z out of the screen). At rest it points UP.
//   - pitchUpDeg: elevation of the selfie camera's axis (+z) above the
//     horizon. Positive = phone leaning back, camera looking up. Equals
//     90 - beta for an upright phone.
//   - rollDeg: elevation of the device's +x axis (right edge as you look at
//     the screen). Positive = right edge raised. The stored JPEG is mirrored,
//     so in the image the tilt appears the other way round.
//   - Missing readings are null, never 0: 0 is a real (level) reading.

export const CAPTURE_SCHEMA = 'sf84.capture.v1';

export type Vec3 = { x: number | null; y: number | null; z: number | null };
export type Stats = { n: number; mean: number; sd: number; min: number; max: number };
export type Permission = 'granted' | 'denied' | 'error' | 'not-required' | 'unavailable';

const DEG = 180 / Math.PI;
const round = (v: number | null, digits = 3) =>
  v === null || !Number.isFinite(v) ? null : Math.round(v * 10 ** digits) / 10 ** digits;

function finiteVec(g: Vec3 | null | undefined): { x: number; y: number; z: number } | null {
  if (!g) return null;
  const { x, y, z } = g;
  if (![x, y, z].every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  return { x: x as number, y: y as number, z: z as number };
}

export function pitchRollFromGravity(g: Vec3 | null | undefined): { pitchUpDeg: number; rollDeg: number } | null {
  const v = finiteVec(g);
  if (!v) return null;
  const n = Math.hypot(v.x, v.y, v.z);
  if (n < 1e-6) return null;
  const clamp = (a: number) => Math.max(-1, Math.min(1, a));
  return { pitchUpDeg: Math.asin(clamp(v.z / n)) * DEG, rollDeg: Math.asin(clamp(v.x / n)) * DEG };
}

/** Unit "up" vector in the device frame from DeviceOrientation beta/gamma (degrees). */
export function gravityFromOrientation(beta: number | null | undefined, gamma: number | null | undefined): Vec3 | null {
  if (typeof beta !== 'number' || typeof gamma !== 'number' || !Number.isFinite(beta) || !Number.isFinite(gamma)) {
    return null;
  }
  const b = beta / DEG;
  const c = gamma / DEG;
  return { x: -Math.cos(b) * Math.sin(c), y: Math.sin(b), z: Math.cos(b) * Math.cos(c) };
}

export function windowStats(values: number[]): Stats | null {
  if (!values.length) return null;
  const n = values.length;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const sd = Math.sqrt(values.reduce((a, b) => a + (b - mean) ** 2, 0) / n);
  const r = (v: number) => round(v) as number;
  return { n, mean: r(mean), sd: r(sd), min: r(Math.min(...values)), max: r(Math.max(...values)) };
}

export type TiltSnapshot = {
  source: 'motion' | 'orientation' | null;
  pitchUpDeg: number | null;
  rollDeg: number | null;
  gravity: Vec3 | null;              // latest raw motion vector, as the browser reported it
  gravitySignFlipped: boolean;       // the platform reported "down"; undone before pitch/roll
  beta: { raw: number | null; smoothed: number | null };
  gamma: number | null;
  window: { ms: number; n: number; pitch: Stats | null; roll: Stats | null; betaRaw: Stats | null } | null;
  permission: Permission;
};

type Sample<T> = { t: number; v: T };

/** Keeps the trailing window of sensor readings so the shutter can record them. */
export class TiltSampler {
  private motion: Sample<{ x: number; y: number; z: number }>[] = [];
  private orient: Sample<{ beta: number; gamma: number }>[] = [];
  private lastBeta: number | null = null;
  private lastGamma: number | null = null;

  constructor(private readonly windowMs = 1000, private readonly keepMs = 2000) {}

  private trim(now: number) {
    while (this.motion.length && now - this.motion[0].t > this.keepMs) this.motion.shift();
    while (this.orient.length && now - this.orient[0].t > this.keepMs) this.orient.shift();
  }

  addMotion(g: Vec3 | null | undefined, now: number) {
    const v = finiteVec(g);
    if (v) this.motion.push({ t: now, v });
    this.trim(now);
  }

  addOrientation(e: { alpha?: number | null; beta?: number | null; gamma?: number | null }, now: number) {
    const beta = typeof e.beta === 'number' && Number.isFinite(e.beta) ? e.beta : null;
    const gamma = typeof e.gamma === 'number' && Number.isFinite(e.gamma) ? e.gamma : null;
    this.lastBeta = beta;
    this.lastGamma = gamma;
    if (beta !== null && gamma !== null) this.orient.push({ t: now, v: { beta, gamma } });
    this.trim(now);
  }

  snapshot(now: number, smoothedBeta: number | null, permission: Permission): TiltSnapshot {
    const inWin = <T,>(s: Sample<T>[]) => s.filter((x) => now - x.t <= this.windowMs);
    const motion = inWin(this.motion);
    const orient = inWin(this.orient);

    let source: TiltSnapshot['source'] = null;
    let angles: { pitchUpDeg: number; rollDeg: number }[] = [];
    let latest: { pitchUpDeg: number; rollDeg: number } | null = null;
    let flipped = false;
    let gravity: Vec3 | null = null;

    if (motion.length) {
      source = 'motion';
      // Capture fires only after the level gate, so the phone is upright and
      // "up" must point along +y. A negative mean y means the platform reports
      // gravity the other way round (seen on some iOS versions).
      flipped = motion.reduce((a, s) => a + s.v.y, 0) < 0;
      const k = flipped ? -1 : 1;
      angles = motion
        .map((s) => pitchRollFromGravity({ x: k * s.v.x, y: k * s.v.y, z: k * s.v.z }))
        .filter((a): a is { pitchUpDeg: number; rollDeg: number } => a !== null);
      latest = angles.length ? angles[angles.length - 1] : null;
      const g = motion[motion.length - 1].v;
      gravity = { x: round(g.x), y: round(g.y), z: round(g.z) };
    } else if (orient.length) {
      source = 'orientation';
      angles = orient
        .map((s) => pitchRollFromGravity(gravityFromOrientation(s.v.beta, s.v.gamma)))
        .filter((a): a is { pitchUpDeg: number; rollDeg: number } => a !== null);
      latest = angles.length ? angles[angles.length - 1] : null;
    }

    const betaRaw = orient.map((s) => s.v.beta);
    return {
      source,
      pitchUpDeg: latest ? round(latest.pitchUpDeg) : null,
      rollDeg: latest ? round(latest.rollDeg) : null,
      gravity,
      gravitySignFlipped: flipped,
      // The page averages e.beta ?? 0, so with no real beta its average is a fake 0.
      beta: { raw: this.lastBeta, smoothed: this.lastBeta === null ? null : smoothedBeta ?? null },
      gamma: this.lastGamma,
      window: source
        ? {
            ms: this.windowMs,
            n: angles.length,
            pitch: windowStats(angles.map((a) => a.pitchUpDeg)),
            roll: windowStats(angles.map((a) => a.rollDeg)),
            betaRaw: windowStats(betaRaw),
          }
        : null,
      permission,
    };
  }
}

export type DeviceInfo = {
  userAgent: string;
  model: string | null;              // UA Client Hints (Chromium on Android); null on iOS
  platform: string | null;
  mobile: boolean | null;
  screen: { width: number; height: number; dpr: number };
};

export type CaptureRecord = {
  schema: string;
  view: string;
  attempt: number;                   // 1-based, per view, this page load
  sessionAttempt: number;            // 1-based, all views, this page load
  rejectedSoFar: number;             // rejections of this view before this attempt
  capturedAt: string;
  device: DeviceInfo;
  image: { width: number | null; height: number | null; mirrored: true; facingMode: 'user' };
  tilt: TiltSnapshot;
  poseOk: boolean | null;
  app: { commit: string; buildTime: string };
};

export function buildCaptureRecord(a: {
  view: string;
  attempt: number;
  sessionAttempt: number;
  rejectedSoFar: number;
  now: Date;
  device: DeviceInfo;
  video: { width: number | null; height: number | null };
  tilt: TiltSnapshot;
  app: { commit: string; buildTime: string };
  poseOk: boolean | null;
}): CaptureRecord {
  return {
    schema: CAPTURE_SCHEMA,
    view: a.view,
    attempt: a.attempt,
    sessionAttempt: a.sessionAttempt,
    rejectedSoFar: a.rejectedSoFar,
    capturedAt: a.now.toISOString(),
    device: a.device,
    // The shutter is a canvas grab of the mirrored selfie preview (react-webcam
    // mirrored={true}); the stored JPEG is mirrored too. Recorded, not undone.
    image: { width: a.video.width, height: a.video.height, mirrored: true, facingMode: 'user' },
    tilt: a.tilt,
    poseOk: a.poseOk,
    app: a.app,
  };
}
